import React, { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { grey, yellow } from "@mui/material/colors";
import { dateTimeMask } from "../../../common/dateMask";
import { DividerBlackOnPrint } from "../../styled";
import LabelToValueHorizontal from "../../labels/LabelToValueHorizontal";
import IconButton from "@mui/material/IconButton";
import PrintIcon from "@mui/icons-material/Print";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import { formatFinance } from "../../../utils/formatters";

const ReportTicket = ({
  filters,
  driverName,
  data: { summary, drivers },
  emissionDate = null,
}) => {
  const summaryComponentRef = useRef();
  const detailedComponentRef = useRef();

  const [summaryTicketHeightInMM, setSummaryTicketHeightInMM] = useState(0);
  const [detailedTicketHeightInMM, setDetailedTicketHeightInMM] = useState(0);
  const [ticketWidthInMM, setTicketWidthInMM] = useState(0);

  // Set up the print hooks at the component top level
  const handleSummaryPrint = useReactToPrint({
    contentRef: summaryComponentRef,
    onPrintError: (errorLocation, error) => {
      console.error(`Print error at ${errorLocation}:`, error);
      dispatchSnackBarMessage(
        `print-error-${Date.now()}`,
        `Erro ao imprimir: ${error.message}`,
        "error"
      );
    },
    pageStyle: `
      @media print {
        @page {
          margin: 0;
          size: auto ${summaryTicketHeightInMM}mm;
        }
        @top-left { content: none; }
        @top-center { content: none; }
        @top-right { content: none; }
        @bottom-left { content: none; }
        @bottom-center { content: none; }
        @bottom-right { content: none; }
      }
    `,
  });

  const handleDetailedPrint = useReactToPrint({
    contentRef: detailedComponentRef,
    onPrintError: (errorLocation, error) => {
      console.error(`Print error at ${errorLocation}:`, error);
      dispatchSnackBarMessage(
        `print-error-${Date.now()}`,
        `Erro ao imprimir: ${error.message}`,
        "error"
      );
    },
    pageStyle: `
      @media print {
        @page {
          margin: 0;
          size: auto ${detailedTicketHeightInMM}mm;
        }
        @top-left { content: none; }
        @top-center { content: none; }
        @top-right { content: none; }
        @bottom-left { content: none; }
        @bottom-center { content: none; }
        @bottom-right { content: none; }
      }
    `,
  });

  useEffect(() => {
    if (summaryComponentRef.current) {
      const boundingRect = summaryComponentRef.current.getBoundingClientRect();

      // Calculating height
      const height = boundingRect.height;
      const pixelsPerMM = 96 / 25.4; // Standard pixel density
      const heightInMM = height / pixelsPerMM;
      setSummaryTicketHeightInMM(heightInMM);

      // Calculating width
      const width = boundingRect.width;
      const widthInMM = width / pixelsPerMM;
      setTicketWidthInMM(widthInMM);
    }
  }, [summaryComponentRef]);

  useEffect(() => {
    if (detailedComponentRef.current) {
      const boundingRect = detailedComponentRef.current.getBoundingClientRect();

      // Calculating height
      const height = boundingRect.height;
      const pixelsPerMM = 96 / 25.4; // Standard pixel density
      const heightInMM = height / pixelsPerMM;
      setDetailedTicketHeightInMM(heightInMM);

      // Calculating width
      const width = boundingRect.width;
      const widthInMM = width / pixelsPerMM;
      setTicketWidthInMM(widthInMM);
    }
  }, [detailedComponentRef]);

  return (
    <Box sx={{ px: 2 }}>
      <Box
        display="flex"
        justifyContent="center"
        sx={{ mb: 1, borderRadius: 4, backgroundColor: "#fff" }}
      >
        <IconButton onClick={handleSummaryPrint} color="primary">
          <PrintIcon />
        </IconButton>
      </Box>

      <Box
        ref={summaryComponentRef}
        sx={{ p: 1, borderRadius: 4, backgroundColor: yellow[100] }}
      >
        <Typography variant="h6" align="center">
          {driverName || "Todos os Motoristas"}
        </Typography>
        <Typography
          variant="subtitle2"
          align="center"
          gutterBottom={!emissionDate}
        >
          De {dateTimeMask(filters.dateStart)} Até{" "}
          {dateTimeMask(filters.dateEnd)}
        </Typography>
        {emissionDate && (
          <Typography variant="subtitle2" align="center" gutterBottom>
            Emitido em {dateTimeMask(emissionDate)}
          </Typography>
        )}
        <DividerBlackOnPrint sx={{ my: 1 }} />

        <Typography variant="h6" align="center" fontWeight={700} gutterBottom>
          Sumário
        </Typography>

        <DividerBlackOnPrint sx={{ my: 1 }} />

        <LabelToValueHorizontal
          label="Pedidos"
          value={summary?.total_orders ?? 0}
          backgroundColor={"transparent"}
        />

        <LabelToValueHorizontal
          label="Corridas"
          value={summary?.total_shipping_routes ?? 0}
          backgroundColor={"transparent"}
        />

        <LabelToValueHorizontal
          label="Corridas Extras"
          value={summary?.total_extra_deliveries ?? 0}
          backgroundColor={"transparent"}
        />

        <LabelToValueHorizontal
          label="Rotas"
          value={summary?.total_route_orders ?? 0}
          backgroundColor={"transparent"}
        />

        <LabelToValueHorizontal
          label="Principais"
          value={summary?.total_main_orders ?? 0}
          backgroundColor={"transparent"}
        />

        <LabelToValueHorizontal
          label="Tx. Entregador"
          value={formatFinance(summary?.total_delivery_tax / 100 ?? 0)}
          backgroundColor={"transparent"}
          isbold
        />

        <DividerBlackOnPrint sx={{ my: 1 }} />
      </Box>
      {drivers.length > 0 && (
        <>
          <Box
            display="flex"
            justifyContent="center"
            sx={{ mt: 3, borderRadius: 4, backgroundColor: "#fff" }}
          >
            <IconButton onClick={handleDetailedPrint} color="primary">
              <PrintIcon />
            </IconButton>
          </Box>
          <Box
            ref={detailedComponentRef}
            sx={{ p: 1, my: 1, borderRadius: 4, backgroundColor: yellow[100] }}
          >
            <Typography variant="h6" align="center" fontWeight={700}>
              Detalhado
            </Typography>
            <Typography
              variant="subtitle2"
              align="center"
              gutterBottom={!emissionDate}
            >
              De {dateTimeMask(filters.dateStart)} Até{" "}
              {dateTimeMask(filters.dateEnd)}
            </Typography>
            {emissionDate && (
              <Typography variant="subtitle2" align="center" gutterBottom>
                Emitido em {dateTimeMask(emissionDate)}
              </Typography>
            )}
            <DividerBlackOnPrint sx={{ my: 1 }} />
            {drivers.map((driver, index) => (
              <Box key={index}>
                <Typography
                  variant="body2"
                  align="center"
                  fontWeight={700}
                  sx={{ color: grey[700] }}
                >
                  {driver.driver_name}
                </Typography>

                <LabelToValueHorizontal
                  label="Pedidos"
                  value={driver?.total_orders ?? 0}
                  backgroundColor={"transparent"}
                />

                <LabelToValueHorizontal
                  label="Corridas Extras"
                  value={driver?.extra_deliveries ?? 0}
                  backgroundColor={"transparent"}
                />

                <LabelToValueHorizontal
                  label="Corridas"
                  value={driver?.total_routes ?? 0}
                  backgroundColor={"transparent"}
                />

                <LabelToValueHorizontal
                  label="Rotas"
                  value={driver?.route_orders ?? 0}
                  backgroundColor={"transparent"}
                />

                <LabelToValueHorizontal
                  label="Principais"
                  value={driver?.main_orders ?? 0}
                  backgroundColor={"transparent"}
                />

                <LabelToValueHorizontal
                  label="Tx. Entregador"
                  value={formatFinance(driver?.delivery_tax_sum / 100 ?? 0)}
                  backgroundColor={"transparent"}
                  isbold
                />
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ReportTicket;
